// Bilateral symmetry for rigs that name their sides in the bone id.
//
// Posing a two-armed character without mirroring is miserable: you set a
// shoulder, then set the other shoulder to the negative of three numbers by
// hand, and you get one of them wrong. This is generic rather than per-game
// because every humanoid rig in the wild suffixes its sides one of about four
// ways, and recognising them costs almost nothing.

/** Suffix pairs, longest first so `-left` is not mistaken for `-l`. */
const SIDE_SUFFIX_PAIRS: [string, string][] = [
  ['_left', '_right'],
  ['-left', '-right'],
  ['.left', '.right'],
  ['_l', '_r'],
  ['-l', '-r'],
  ['.l', '.r'],
  ['.L', '.R'],
];

/** Prefix pairs, longest first - the engine's own Neutral Biped rig (see
 * `NeutralBiped.tsx`'s `CORE_RIG_BONES`, e.g. `left-arm`/`right-arm`) names
 * its sides this way rather than suffixing them. */
const SIDE_PREFIX_PAIRS: [string, string][] = [
  ['left_', 'right_'],
  ['left-', 'right-'],
  ['left.', 'right.'],
  ['l_', 'r_'],
  ['l-', 'r-'],
];

/**
 * The bone on the other side of the body, or null when the bone sits on the
 * centre line (a spine, a head) and has no twin.
 */
export function mirrorBoneId(boneId: string): string | null {
  for (const [left, right] of SIDE_SUFFIX_PAIRS) {
    if (boneId.endsWith(left)) return boneId.slice(0, -left.length) + right;
    if (boneId.endsWith(right)) return boneId.slice(0, -right.length) + left;
  }
  for (const [left, right] of SIDE_PREFIX_PAIRS) {
    if (boneId.startsWith(left)) return right + boneId.slice(left.length);
    if (boneId.startsWith(right)) return left + boneId.slice(right.length);
  }
  return null;
}

/**
 * A rotation reflected across the body's plane of symmetry.
 *
 * Pitch survives: a nod is a nod on both sides. Yaw and roll invert, because
 * they turn about axes the mirror reverses. This is the correct reflection for
 * a rig whose left and right chains are built as mirror images, which is what
 * `mirrorBoneId` implies.
 */
export function mirrorRotation(rotation: [number, number, number]): [number, number, number] {
  return [rotation[0], -rotation[1], -rotation[2]];
}

/** True when the rig names sides at all, i.e. mirroring is meaningful. */
export function rigHasSymmetry(boneIds: readonly string[]): boolean {
  return boneIds.some((id) => mirrorBoneId(id) !== null);
}
